import { useEffect, useState } from 'react'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import type { Appearance } from '@stripe/stripe-js'
import { getStripe } from '../../lib/stripe'
import { ClubButton } from './ClubButton'

/**
 * Lodging a card against an existing standing (§12.7) — a SetupIntent, which
 * collects a card WITHOUT charging it.
 *
 * NOT exported from components/golf/index.ts, for the same reason
 * DuesPaymentForm is not: that barrel is imported by every club screen and by
 * clubPages.test.tsx, so exporting this would drag Stripe.js into all of their
 * module graphs. The account screen imports it by path.
 */

// The golf tokens from entrypoints/fireside.css, transcribed by hand — Stripe
// paints inside its own iframe and takes plain CSS colours only. `flat` on
// clubhouse paper, never the wallet form's `night`: the two design systems
// share no tokens and the firelight object on this ground is unreadable.
const appearance: Appearance = {
  theme: 'flat',
  variables: {
    colorPrimary: '#2c5a41', // fairway
    colorBackground: '#fbf7ee', // parchment
    colorText: '#2a251c', // ink
    colorTextSecondary: '#6b6353', // ink-soft
    colorDanger: '#b0392c', // flag
    borderRadius: '3px', // radius-club
    fontFamily: '"Hanken Grotesk Variable", ui-sans-serif, system-ui, sans-serif',
  },
}

interface CardOnFileFormProps {
  clientSecret: string
  /** Absolute /clubhouse/account URL — where a 3DS challenge returns. */
  returnUrl: string
  /**
   * Fired on IN-PLACE confirmation, with the SetupIntent id. This advances the
   * UI only: `payment_method_on_file` becomes true when the SERVER re-reads the
   * subscription, never because the browser said so.
   */
  onConfirmed: (setupIntentId: string) => void
  onCancel: () => void
}

export function CardOnFileForm({ clientSecret, returnUrl, onConfirmed, onCancel }: CardOnFileFormProps) {
  const [unavailable, setUnavailable] = useState(false)

  useEffect(() => {
    let active = true
    getStripe().then(
      (stripe) => {
        if (active && !stripe) setUnavailable(true)
      },
      () => {
        if (active) setUnavailable(true)
      },
    )
    return () => {
      active = false
    }
  }, [])

  if (unavailable) {
    return (
      <div className="space-y-5">
        <p className="text-[13.5px] text-ink-soft">
          The front desk can&rsquo;t take a card in this environment — payments aren&rsquo;t
          configured.
        </p>
        <ClubButton variant="ghost" size="sm" onClick={onCancel}>
          Back
        </ClubButton>
      </div>
    )
  }

  return (
    <Elements stripe={getStripe()} options={{ clientSecret, appearance }}>
      <CardFormInner returnUrl={returnUrl} onConfirmed={onConfirmed} onCancel={onCancel} />
    </Elements>
  )
}

function CardFormInner({ returnUrl, onConfirmed, onCancel }: Omit<CardOnFileFormProps, 'clientSecret'>) {
  const stripe = useStripe()
  const elements = useElements()
  const [ready, setReady] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!stripe || !elements || submitting) return
    setSubmitting(true)
    setError('')
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: returnUrl },
      // Stay in-page for plain cards; only a 3DS challenge leaves for returnUrl.
      redirect: 'if_required',
    })
    if (result.error) {
      const { type, message } = result.error
      setError(
        (type === 'card_error' || type === 'validation_error') && message
          ? message
          : 'Something went wrong lodging that card — try again.',
      )
      setSubmitting(false)
      return
    }
    onConfirmed(result.setupIntent?.id ?? '')
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" aria-label="Card details">
      {!ready && <p className="py-6 text-center text-[13.5px] text-ink-soft">Opening the desk&hellip;</p>}
      <PaymentElement onReady={() => setReady(true)} />
      {error && (
        <p className="text-[13.5px] text-flag" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-4">
        <ClubButton type="submit" variant="flag" disabled={!stripe || !elements || !ready || submitting}>
          {submitting ? 'Lodging…' : 'Lodge the card'}
        </ClubButton>
        <ClubButton type="button" variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
          Back
        </ClubButton>
      </div>
    </form>
  )
}
